import type {
  AssetValidationFailure,
  AssetValidationResult,
} from '@terra/shared-utils';

export type InvalidAssetEntry = {
  value: string;
  reason: AssetValidationFailure;
  message: string;
};

export const parseAssetEntries = (value: string): string[] =>
  value
    .split(/[\n,]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

export const hasAnyAssets = ({
  domains,
  netblocks,
}: {
  domains: string[];
  netblocks: string[];
}): boolean => domains.length + netblocks.length > 0;

export const collectInvalidAssets = ({
  values,
  validator,
}: {
  values: string[];
  validator: (value: string) => AssetValidationResult;
}): InvalidAssetEntry[] =>
  values.flatMap((value) => {
    const result = validator(value);

    return result.ok
      ? []
      : [{ value, reason: result.reason, message: result.message }];
  });

export type ResolveNewAssetEntriesResult =
  | { ok: true; values: string[] }
  | { ok: false; message: string };

export const resolveNewAssetEntries = ({
  input,
  validator,
  existingValues,
  duplicateErrorMessage,
}: {
  input: string;
  validator: (value: string) => AssetValidationResult;
  existingValues: readonly string[];
  duplicateErrorMessage: string;
}): ResolveNewAssetEntriesResult =>
  parseAssetEntries(input).reduce<ResolveNewAssetEntriesResult>(
    (accumulator, entry) => {
      if (!accumulator.ok) {
        return accumulator;
      }

      const result = validator(entry);

      if (!result.ok) {
        return { ok: false, message: result.message };
      }

      if (
        existingValues.includes(result.value) ||
        accumulator.values.includes(result.value)
      ) {
        return { ok: false, message: duplicateErrorMessage };
      }

      return { ok: true, values: [...accumulator.values, result.value] };
    },
    { ok: true, values: [] },
  );
